import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  DatatableWrapper,
  TableBody,
  TableHeader,
  Pagination,
} from "react-bs-datatable";
import { Table, Button, Image, Form } from "react-bootstrap";
import { StatusBadge, TablePagination, TableSearchField } from "CommonComponent";
import { getFormatedDateTime, trimAndLimit } from "utils/general";
import AccessRights from "utils/AccessRights";

const headers = [
  { prop: "name", title: "Name", isSortable: false },
  { prop: "description", title: "Description", isSortable: true },
  { prop: "lastmodified", title: "Last Modified", isSortable: true },
  { prop: "status", title: "Status", isSortable: true },
  { prop: "actions", title: "Actions", isSortable: true },
];

const DataTable = ({
  showViewPopup,
  showUpdateForm,
  setDeleteRowId,
  setDeleteModalShow,
}) => {
  const dispatch = useDispatch();
  const session = useSelector((state) => state.session);
  const rolesState = useSelector((state) => state.rolesState);

  const [filteredData, setFilteredData] = useState([]);
  const [filterValue, setFilterValue] = useState("");
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  useEffect(() => {
    setData(rolesState?.rolesList || []);
  }, [rolesState.rolesList]);

  useEffect(() => {
    const filtered = data.filter((row) => {
      return Object.values(row).some(
        (value) =>
          value != null &&
          value.toString().toLowerCase().includes(filterValue.toLowerCase())
      );
    });
    setFilteredData(filtered);
  }, [filterValue, data]);

  const handleFilterChange = (e) => {
    setFilterValue(e.target.value);
  };

  const toggleStatus = (is_active) => (
    <StatusBadge
      label={is_active === "y" ? "Active" : "Inactive"}
      variant={is_active === "y" ? "active" : "inactive"}
    />
  );

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <>
      <DatatableWrapper
        body={currentData}
        headers={headers}
        paginationOptions={{
          rowsPerPage,
          rowsPerPageOption: [5, 10, 15, 20],
        }}
      >
        <div>
          <TableSearchField
            value={filterValue}
            onChange={handleFilterChange}
            className="mb-3"
          />
        </div>

        <Table className="pt-5 px-4">
          <TableHeader />
          <TableBody>
            {currentData.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <div className="empty-state py-4" role="status">
                    <img src="/assets/images/emptyStateTable.png" alt="" className="empty-state__image" aria-hidden="true" />
                    <p className="empty-state__title">No Records Found</p>
                    <p className="empty-state__message">There is nothing to display yet. Try adjusting your search.</p>
                  </div>
                </td>
              </tr>
            ) : (
            currentData.map((row) => (
              <tr key={row.id}>
                <td className="td-pad">{row.name}</td>
                <td className="td-pad">{trimAndLimit(row.description)}</td>
                <td className="td-pad">
                  {getFormatedDateTime(row.date_last_modified)}
                </td>
                <td className="td-pad">{toggleStatus(row.is_active)}</td>
                <td className="form-action-width">
                  <Button
                    variant="none"
                    className="border-0"
                    onClick={() => showViewPopup(row.id)}
                  >
                    <Image src="/assets/images/view-icon.svg" />
                  </Button>

                  {AccessRights(
                    session.current_page_permissions,
                    "edit",
                    row.created_by
                  ) &&
                    row.is_deleted === "n" && (
                      <Button
                        className="border-0"
                        variant="none"
                        onClick={() => showUpdateForm(row.id)}
                      >
                        <Image src="/assets/images/edit-icon.svg" />
                      </Button>
                    )}

                  {AccessRights(
                    session.current_page_permissions,
                    "delete",
                    row.created_by
                  ) &&
                    row.is_deleted === "n" && (
                      <Button
                        variant="none"
                        className="border-0"
                        onClick={() => {
                          setDeleteRowId(row.id);
                          setDeleteModalShow(true);
                        }}
                      >
                        <Image src="/assets/images/delete-icon.svg" />
                      </Button>
                    )}
                </td>
              </tr>
            )))}
          </TableBody>
        </Table>

        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={rowsPerPage}
          pageSizeOptions={[5, 10, 20, 50]}
          onPageChange={handlePageChange}
          onPageSizeChange={(size) => {
            setRowsPerPage(size);
            setCurrentPage(1);
          }}
        />
      </DatatableWrapper>
    </>
  );
};

export default DataTable;
